import { getTemplateInstanceSlots } from '@/components/visual-editor/workflow/template-instance-runtime';
import {
  buildWorkflowAreaNodeBox,
  resolveWorkflowAreaSpatialNodeIds,
  WORKFLOW_AREA_FRAME_PADDING,
  type WorkflowAreaNodeBox,
} from '@/components/visual-editor/workflow/workflowAreaGeometry';
import type { CanvasNode, CanvasTemplateInstance } from '@/types/visual-editor';

export interface WorkflowAreaRect {
  groupId: string;
  nodeIds: string[];
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface WorkflowAreaTemplatePair {
  source: string;
  target: string;
}

export interface WorkflowAreaTemplateComponent {
  groupId: string;
  templateNodeIds: string[];
  templatePairs: WorkflowAreaTemplatePair[];
}

function normalizeLineageSourceNodeIds(sourceNodeIds: Array<string | null | undefined>): string[] {
  const seen = new Set<string>();
  const normalized: string[] = [];

  sourceNodeIds.forEach((sourceNodeId) => {
    if (typeof sourceNodeId !== 'string') {
      return;
    }

    const trimmed = sourceNodeId.trim();
    if (!trimmed || seen.has(trimmed)) {
      return;
    }

    seen.add(trimmed);
    normalized.push(trimmed);
  });

  return normalized;
}

function getTemplateInstanceOutputNodeIds(templateInstance: CanvasTemplateInstance): string[] {
  const seenRunIds = new Set<string>();
  const runs = [templateInstance.activeRun, ...(templateInstance.runHistory || [])].filter((run): run is NonNullable<CanvasTemplateInstance['activeRun']> => {
    if (!run || seenRunIds.has(run.id)) {
      return false;
    }
    seenRunIds.add(run.id);
    return true;
  });

  return runs.flatMap((run) => run.outputNodeIds || (run.outputNodeId ? [run.outputNodeId] : []));
}

function compareNodeIds(nodeById: Map<string, CanvasNode>, leftId: string, rightId: string): number {
  const leftNode = nodeById.get(leftId);
  const rightNode = nodeById.get(rightId);
  if (!leftNode || !rightNode) {
    return leftId.localeCompare(rightId);
  }
  if (leftNode.position.x !== rightNode.position.x) {
    return leftNode.position.x - rightNode.position.x;
  }
  if (leftNode.position.y !== rightNode.position.y) {
    return leftNode.position.y - rightNode.position.y;
  }
  return leftId.localeCompare(rightId);
}

function buildTemplateWorkflowGroupId(nodeIds: Iterable<string>): string | null {
  const canonicalNodeIds = [...new Set(
    Array.from(nodeIds).filter((nodeId): nodeId is string => typeof nodeId === 'string' && nodeId.length > 0),
  )].sort((leftId, rightId) => leftId.localeCompare(rightId));

  return canonicalNodeIds[0] ? `template-workflow-group-${canonicalNodeIds[0]}` : null;
}

function getWorkflowAreaTemplateNodes(
  nodes: CanvasNode[],
): Array<CanvasNode & { templateInstance: CanvasTemplateInstance }> {
  return nodes.filter(
    (node): node is CanvasNode & { templateInstance: CanvasTemplateInstance } => (
      Boolean(node.templateInstance)
      && !node.groupChildIds?.length
      && !node.groupId
    ),
  );
}

function resolveWorkflowAreaComponentGroupId(
  templateNodeIds: string[],
  nodeById: Map<string, CanvasNode>,
): string | null {
  const canonicalGroupId = buildTemplateWorkflowGroupId(templateNodeIds);
  const storedGroupIds = [...new Set(
    templateNodeIds
      .map((nodeId) => nodeById.get(nodeId)?.workflowAreaGroupId?.trim())
      .filter((groupId): groupId is string => Boolean(groupId)),
  )];

  if (storedGroupIds.length === 1) {
    return storedGroupIds[0];
  }
  if (canonicalGroupId && storedGroupIds.includes(canonicalGroupId)) {
    return canonicalGroupId;
  }

  return canonicalGroupId ?? storedGroupIds[0] ?? null;
}

export function getWorkflowAreaTemplateComponents(
  nodes: CanvasNode[],
  canvasEdges: Array<{ source: string; target: string }>,
): WorkflowAreaTemplateComponent[] {
  const templateNodes = getWorkflowAreaTemplateNodes(nodes);
  if (templateNodes.length === 0) {
    return [];
  }

  const templateNodeById = new Map(templateNodes.map((node) => [node.id, node] as const));
  const nodeById = new Map(nodes.filter((node) => node.position).map((node) => [node.id, node] as const));
  const templateNodeIdSet = new Set(templateNodes.map((node) => node.id));
  const uniquePairs: WorkflowAreaTemplatePair[] = [];
  const pairKeys = new Set<string>();
  const neighbors = new Map<string, Set<string>>();

  templateNodes.forEach((node) => {
    neighbors.set(node.id, new Set<string>());
  });

  canvasEdges.forEach((edge) => {
    if (!templateNodeIdSet.has(edge.source) || !templateNodeIdSet.has(edge.target) || edge.source === edge.target) {
      return;
    }

    const pairKey = `${edge.source}::${edge.target}`;
    if (!pairKeys.has(pairKey)) {
      pairKeys.add(pairKey);
      uniquePairs.push({ source: edge.source, target: edge.target });
    }

    neighbors.get(edge.source)?.add(edge.target);
    neighbors.get(edge.target)?.add(edge.source);
  });

  const templateNodeIdsByStoredGroupId = new Map<string, string[]>();
  templateNodes.forEach((node) => {
    const groupId = node.workflowAreaGroupId?.trim();
    if (!groupId) {
      return;
    }
    const groupNodeIds = templateNodeIdsByStoredGroupId.get(groupId) ?? [];
    groupNodeIds.push(node.id);
    templateNodeIdsByStoredGroupId.set(groupId, groupNodeIds);
  });

  templateNodeIdsByStoredGroupId.forEach((groupNodeIds) => {
    const orderedNodeIds = [...new Set(groupNodeIds)]
      .sort((leftId, rightId) => compareNodeIds(nodeById, leftId, rightId));
    if (orderedNodeIds.length <= 1) {
      return;
    }

    const [anchorNodeId, ...siblingNodeIds] = orderedNodeIds;
    siblingNodeIds.forEach((nodeId) => {
      neighbors.get(anchorNodeId)?.add(nodeId);
      neighbors.get(nodeId)?.add(anchorNodeId);
    });
  });

  const orderedTemplateIds = templateNodes
    .map((node) => node.id)
    .sort((leftId, rightId) => compareNodeIds(nodeById, leftId, rightId));
  const visited = new Set<string>();
  const components: WorkflowAreaTemplateComponent[] = [];

  orderedTemplateIds.forEach((startId) => {
    if (visited.has(startId)) {
      return;
    }

    const componentIds: string[] = [];
    const queue = [startId];
    visited.add(startId);

    while (queue.length > 0) {
      const currentId = queue.shift();
      if (!currentId) {
        continue;
      }

      componentIds.push(currentId);
      const currentNeighbors = [...(neighbors.get(currentId) ?? [])]
        .sort((leftId, rightId) => compareNodeIds(nodeById, leftId, rightId));
      currentNeighbors.forEach((neighborId) => {
        if (visited.has(neighborId)) {
          return;
        }
        visited.add(neighborId);
        queue.push(neighborId);
      });
    }

    componentIds.sort((leftId, rightId) => compareNodeIds(nodeById, leftId, rightId));
    const componentIdSet = new Set(componentIds);
    const componentPairs = uniquePairs.filter((pair) => componentIdSet.has(pair.source) && componentIdSet.has(pair.target));
    const hasStoredGroupId = componentIds.some((nodeId) => Boolean(templateNodeById.get(nodeId)?.workflowAreaGroupId?.trim()));
    if (!hasStoredGroupId && componentPairs.length === 0) {
      return;
    }

    const groupId = resolveWorkflowAreaComponentGroupId(componentIds, nodeById);
    if (!groupId) {
      return;
    }

    components.push({
      groupId,
      templateNodeIds: componentIds,
      templatePairs: componentPairs,
    });
  });

  return components;
}

function getWorkflowAreaNodeBox(node: CanvasNode): WorkflowAreaNodeBox {
  return buildWorkflowAreaNodeBox({
    id: node.id,
    x: node.position.x,
    y: node.position.y,
    width: node.mediaType === '3d_model' ? 260 : 180,
    height: 220,
    type: node.templateInstance ? 'template' : 'clip',
  });
}

export function getWorkflowAreaDirectEdgeNodeIdsByGroup(params: {
  components: WorkflowAreaTemplateComponent[];
  nodes: CanvasNode[];
  canvasEdges: Array<{ source: string; target: string }>;
  liveNodeIds: ReadonlySet<string>;
}): Record<string, string[]> {
  const nodeById = new Map(params.nodes.map((node) => [node.id, node] as const));
  const groupIdByTemplateNodeId = new Map<string, string>();
  const templateNodeIdsByGroup = new Map<string, Set<string>>();
  const referencingGroupIdsByNodeId = new Map<string, Set<string>>();

  params.components.forEach((component) => {
    const templateNodeIdSet = new Set(component.templateNodeIds);
    templateNodeIdsByGroup.set(component.groupId, templateNodeIdSet);
    component.templateNodeIds.forEach((nodeId) => {
      groupIdByTemplateNodeId.set(nodeId, component.groupId);
    });
  });

  const addReference = (groupId: string, nodeId: string) => {
    if (!params.liveNodeIds.has(nodeId)) {
      return;
    }

    const node = nodeById.get(nodeId);
    if (!node || node.templateInstance || templateNodeIdsByGroup.get(groupId)?.has(nodeId)) {
      return;
    }

    const groupIds = referencingGroupIdsByNodeId.get(nodeId) ?? new Set<string>();
    groupIds.add(groupId);
    referencingGroupIdsByNodeId.set(nodeId, groupIds);
  };

  params.canvasEdges.forEach((edge) => {
    const sourceGroupId = groupIdByTemplateNodeId.get(edge.source);
    if (sourceGroupId) {
      addReference(sourceGroupId, edge.target);
    }

    const targetGroupId = groupIdByTemplateNodeId.get(edge.target);
    if (targetGroupId) {
      addReference(targetGroupId, edge.source);
    }
  });

  const nodeIdsByGroup: Record<string, string[]> = {};
  params.components.forEach((component) => {
    nodeIdsByGroup[component.groupId] = [];
  });

  referencingGroupIdsByNodeId.forEach((groupIds, nodeId) => {
    if (groupIds.size === 1) {
      const [groupId] = [...groupIds];
      nodeIdsByGroup[groupId]?.push(nodeId);
      return;
    }

    const explicitGroupId = nodeById.get(nodeId)?.workflowAreaGroupId?.trim();
    if (explicitGroupId && groupIds.has(explicitGroupId)) {
      nodeIdsByGroup[explicitGroupId]?.push(nodeId);
    }
  });

  return nodeIdsByGroup;
}

export function getWorkflowAreaRects(
  nodes: CanvasNode[],
  canvasEdges: Array<{ source: string; target: string }>,
): WorkflowAreaRect[] {
  if (nodes.length === 0) {
    return [];
  }

  const liveNodeIds = new Set(nodes.map((node) => node.id));
  const nodeById = new Map(nodes.filter((node) => node.position).map((node) => [node.id, node] as const));
  const nodeBoxById = new Map(
    [...nodeById.values()].map((node) => [node.id, getWorkflowAreaNodeBox(node)] as const),
  );
  const allNodeBoxes = [...nodeBoxById.values()];
  const components = getWorkflowAreaTemplateComponents(nodes, canvasEdges);
  if (components.length === 0) {
    return [];
  }
  const directEdgeNodeIdsByGroup = getWorkflowAreaDirectEdgeNodeIdsByGroup({
    components,
    nodes,
    canvasEdges,
    liveNodeIds,
  });

  const outputNodeIdsByGroup: Record<string, string[]> = {};
  const referencingGroupIdsByNodeId = new Map<string, Set<string>>();

  components.forEach((component) => {
    const outputIds = new Set<string>();

    component.templateNodeIds.forEach((nodeId) => {
      const templateNode = nodeById.get(nodeId);
      const templateInstance = templateNode?.templateInstance;
      if (!templateInstance) {
        return;
      }

      getTemplateInstanceOutputNodeIds(templateInstance).forEach((outputNodeId) => {
        if (liveNodeIds.has(outputNodeId)) {
          outputIds.add(outputNodeId);
        }
      });

      normalizeLineageSourceNodeIds(
        getTemplateInstanceSlots(templateInstance).map((slot) => templateInstance.values[slot.key]?.sourceNodeId),
      )
        .filter((sourceNodeId) => liveNodeIds.has(sourceNodeId))
        .forEach((sourceNodeId) => {
          const groupIds = referencingGroupIdsByNodeId.get(sourceNodeId) ?? new Set<string>();
          groupIds.add(component.groupId);
          referencingGroupIdsByNodeId.set(sourceNodeId, groupIds);
        });
    });

    outputNodeIdsByGroup[component.groupId] = [...outputIds];
  });

  const exclusiveInputNodeIdsByGroup: Record<string, string[]> = {};
  components.forEach((component) => {
    exclusiveInputNodeIdsByGroup[component.groupId] = [];
  });
  referencingGroupIdsByNodeId.forEach((groupIds, nodeId) => {
    if (groupIds.size !== 1) {
      return;
    }
    const [groupId] = [...groupIds];
    exclusiveInputNodeIdsByGroup[groupId]?.push(nodeId);
  });

  return components.flatMap((component) => {
    const anchorNodeIds = component.templateNodeIds.filter((nodeId) => nodeBoxById.has(nodeId));
    const anchorNodeIdSet = new Set(anchorNodeIds);
    const candidateNodeIds = new Set<string>();

    (outputNodeIdsByGroup[component.groupId] || []).forEach((nodeId) => {
      if (nodeBoxById.has(nodeId) && !anchorNodeIdSet.has(nodeId)) {
        candidateNodeIds.add(nodeId);
      }
    });
    (exclusiveInputNodeIdsByGroup[component.groupId] || []).forEach((nodeId) => {
      if (nodeBoxById.has(nodeId) && !anchorNodeIdSet.has(nodeId)) {
        candidateNodeIds.add(nodeId);
      }
    });
    (directEdgeNodeIdsByGroup[component.groupId] || []).forEach((nodeId) => {
      if (nodeBoxById.has(nodeId) && !anchorNodeIdSet.has(nodeId)) {
        candidateNodeIds.add(nodeId);
      }
    });

    nodes
      .filter((node) => node.workflowAreaGroupId === component.groupId && !anchorNodeIdSet.has(node.id) && liveNodeIds.has(node.id))
      .forEach((node) => {
        if (nodeBoxById.has(node.id)) {
          candidateNodeIds.add(node.id);
        }
      });

    const resolved = resolveWorkflowAreaSpatialNodeIds({
      anchorNodeIds,
      candidateNodeIds: [...candidateNodeIds],
      nodeBoxById,
      allNodeBoxes,
      padding: WORKFLOW_AREA_FRAME_PADDING,
    });

    if (!resolved.bounds) {
      return [];
    }

    return [{
      groupId: component.groupId,
      nodeIds: resolved.nodeIds,
      ...resolved.bounds,
    }];
  });
}

export function getWorkflowAreaExcludedRects(
  nodes: CanvasNode[],
  canvasEdges: Array<{ source: string; target: string }>,
): Array<{ x: number; y: number; width: number; height: number }> {
  return getWorkflowAreaRects(nodes, canvasEdges).map(({ x, y, width, height }) => ({
    x,
    y,
    width,
    height,
  }));
}

export function getWorkflowPlacementExcludedRects(
  nodes: CanvasNode[],
  canvasEdges: Array<{ source: string; target: string }>,
  anchorNodeId?: string | null,
): Array<{ x: number; y: number; width: number; height: number }> {
  const workflowAreaRects = getWorkflowAreaRects(nodes, canvasEdges);
  if (!anchorNodeId) {
    return workflowAreaRects.map(({ x, y, width, height }) => ({ x, y, width, height }));
  }

  const allowedGroupId = workflowAreaRects.find((rect) => rect.nodeIds.includes(anchorNodeId))?.groupId;
  return workflowAreaRects
    .filter((rect) => rect.groupId !== allowedGroupId)
    .map(({ x, y, width, height }) => ({ x, y, width, height }));
}